import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError, fmtDate } from "@/lib/api";
import { Stars } from "@/components/landing/Stars";

const STATUS = {
  en_attente: ["En attente", "bg-slate-100 text-slate-700"],
  approuve: ["Approuvé", "bg-emerald-100 text-emerald-800"],
  rejete: ["Rejeté", "bg-orange-100 text-orange-800"],
};

export const ReviewsTab = () => {
  const [filter, setFilter] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/reviews", { params: filter ? { status: filter } : {} });
      setReviews(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (r, status) => {
    try {
      await adminApi.patch(`/reviews/${r.review_id}`, { status });
      toast.success(status === "approuve" ? "Avis approuvé et publié" : "Avis rejeté");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <select data-testid="reviews-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="field-input max-w-xs">
        <option value="">Tous les avis</option>
        {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <div className="grid gap-4">
        {reviews.map((r) => (
          <article key={r.review_id} data-testid={`admin-review-${r.review_id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <Stars value={r.rating} />
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
              <span className="text-xs text-slate-500">Commande <strong className="font-mono">{r.order_id}</strong> — {fmtDate(r.created_at)} — {r.display_name}{r.consent ? " — accepte d'être recontacté" : ""}</span>
            </div>
            <h3 className="font-display font-bold text-slate-900">{r.title}</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line">{r.comment}</p>
            <div className="flex gap-2 pt-1">
              <button type="button" disabled={r.status === "approuve"} onClick={() => moderate(r, "approuve")} data-testid={`review-approve-${r.review_id}`}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors">Approuver</button>
              <button type="button" disabled={r.status === "rejete"} onClick={() => moderate(r, "rejete")} data-testid={`review-reject-${r.review_id}`}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-orange-100 text-orange-800 hover:bg-orange-200 disabled:opacity-40 transition-colors">Rejeter</button>
            </div>
          </article>
        ))}
        {!loading && reviews.length === 0 && <p className="text-slate-500 text-center py-10 bg-white rounded-2xl border border-slate-200">Aucun avis.</p>}
      </div>
    </div>
  );
};
